import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Company } from 'Deer/intent/Reviews/slice/ReviewsIntentSlice';
import { IntentValuesType } from 'Deer/intent/types/IntentCommonTypes';
import { useSelector } from 'react-redux';

export type GoalsIntentType = {
  id: string;
  label: string;
  onClick: () => void;
  visible: () => boolean;
  value: {
    myData?: IntentValuesType;
    team?: {
      directReports: IntentValuesType;
      allReports: IntentValuesType;
      view?: IntentValuesType;
    };
    company?: IntentValuesType;
  };
  applicableGroupByValues?: {
    value: string;
    label: string;
  }[];
  showGroupByDropdown?: boolean;
  applicableLayoutValues?: string[];
  showLayoutDropdown?: boolean;
  applicableOKRViewValues?: {
    value: string;
    label: string;
    onClick: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Icon: (props: any) => React.JSX.Element;
  }[];
  showOKRViewDropdown?: boolean;
  showTimelineDropdown?: boolean;
  category: 'Manage' | 'Analyze' | 'Checkin' | 'Configure' | 'Create';
  showGoalOwnershipFilter?: boolean;
  isDefault?: boolean;
  timelineDropdownOnChangeCallback?: (onChange: () => void) => void;
  ownershipDropdownOnChangeCallback?: (onChange: () => void) => void;
  hasMetaFilters?: boolean;
};

const initialState: {
  selectedIntent: null | GoalsIntentType;
  selectedCompanies: Company[];
  allCompanies: Company[];
  selectedLocations: string[];
  allLocations: string[];
  selectedCompaniesForGroupByIndividual: Company[];
  allCompaniesForGroupByIndividual: Company[];
  selectedLocationsForGroupByIndividual: string[];
  allLocationsForGroupByIndividual: string[];
  selectedGroupByViewType: 'OBJECTIVE' | 'INDIVIDUAL';
} = {
  selectedIntent: null,
  selectedCompanies: [],
  allCompanies: [],
  selectedLocations: [],
  allLocations: [],
  selectedCompaniesForGroupByIndividual: [],
  allCompaniesForGroupByIndividual: [],
  selectedLocationsForGroupByIndividual: [],
  allLocationsForGroupByIndividual: [],
  selectedGroupByViewType: 'OBJECTIVE',
};

export const goalsIntentSlice = createSlice({
  name: 'goalsIntent',
  initialState,
  reducers: {
    setSelectedGoalsIntent: (state, action: PayloadAction<GoalsIntentType>) => {
      const newState = { ...state };
      newState.selectedIntent = action.payload;
      return newState;
    },
    setSelectedCompanies: (state, action: PayloadAction<Company[]>) => {
      state.selectedCompanies = action.payload;
    },
    setAllCompanies: (state, action: PayloadAction<Company[]>) => {
      state.allCompanies = action.payload;
    },
    setSelectedLocations: (state, action: PayloadAction<string[]>) => {
      state.selectedLocations = action.payload;
    },
    setAllLocations: (state, action: PayloadAction<string[]>) => {
      state.allLocations = action.payload;
    },
    setSelectedCompaniesForGroupByIndividual: (state, action: PayloadAction<Company[]>) => {
      state.selectedCompaniesForGroupByIndividual = action.payload;
    },
    setAllCompaniesForGroupByIndividual: (state, action: PayloadAction<Company[]>) => {
      state.allCompaniesForGroupByIndividual = action.payload;
    },
    setSelectedLocationsForGroupByIndividual: (state, action: PayloadAction<string[]>) => {
      state.selectedLocationsForGroupByIndividual = action.payload;
    },
    setAllLocationsForGroupByIndividual: (state, action: PayloadAction<string[]>) => {
      state.allLocationsForGroupByIndividual = action.payload;
    },
    setSelectedGroupByViewType: (state, action: PayloadAction<'OBJECTIVE' | 'INDIVIDUAL'>) => {
      state.selectedGroupByViewType = action.payload;
    },
  },
});

export const {
  setSelectedGoalsIntent,
  setSelectedCompanies,
  setAllCompanies,
  setSelectedLocations,
  setAllLocations,
  setSelectedCompaniesForGroupByIndividual,
  setAllCompaniesForGroupByIndividual,
  setSelectedLocationsForGroupByIndividual,
  setAllLocationsForGroupByIndividual,
  setSelectedGroupByViewType,
} = goalsIntentSlice.actions;
export default goalsIntentSlice.reducer;

export const useSelectGoalsSelectedIntent = () =>
  useSelector((state: { goalsIntent: typeof initialState }) => state.goalsIntent.selectedIntent);

export const useSelectedGoalsCompanies = () =>
  useSelector((state: { goalsIntent: typeof initialState }) => state.goalsIntent.selectedCompanies);

export const useAllGoalsCompanies = () =>
  useSelector((state: { goalsIntent: typeof initialState }) => state.goalsIntent.allCompanies);

export const useSelectedGoalsLocations = () =>
  useSelector((state: { goalsIntent: typeof initialState }) => state.goalsIntent.selectedLocations);

export const useAllGoalsLocations = () =>
  useSelector((state: { goalsIntent: typeof initialState }) => state.goalsIntent.allLocations);

export const useSelectedGoalsCompaniesForGroupByIndividual = () =>
  useSelector((state: { goalsIntent: typeof initialState }) => state.goalsIntent.selectedCompaniesForGroupByIndividual);

export const useAllGoalsCompaniesForGroupByIndividual = () =>
  useSelector((state: { goalsIntent: typeof initialState }) => state.goalsIntent.allCompaniesForGroupByIndividual);

export const useSelectedGoalsLocationsForGroupByIndividual = () =>
  useSelector((state: { goalsIntent: typeof initialState }) => state.goalsIntent.selectedLocationsForGroupByIndividual);

export const useAllGoalsLocationsForGroupByIndividual = () =>
  useSelector((state: { goalsIntent: typeof initialState }) => state.goalsIntent.allLocationsForGroupByIndividual);

export const useSelectedGoalsGroupByViewType = () =>
  useSelector((state: { goalsIntent: typeof initialState }) => state.goalsIntent.selectedGroupByViewType);
